import { atomWithStorage, createJSONStorage } from 'jotai/utils';

/**
 * SSR-safe JSON storage factory. During server render there is no `window`, so
 * reads short-circuit to the atom's initial value and writes are no-ops.
 * Typed generically so any atomWithStorage<T> can share it.
 */
const createSsrSafeStorage = <T>() =>
  createJSONStorage<T>(() => {
    if (typeof window === 'undefined') {
      return {
        getItem: () => null,
        setItem: () => undefined,
        removeItem: () => undefined,
      };
    }
    return window.localStorage;
  });

/**
 * Threshold (inclusive) at which the Evidence power-user banner becomes eligible
 * to show — and simultaneously at which the in-session `EvidenceProTrialBanner`
 * (the "You've got X days left in your free Clinician Trial" banner) is suppressed.
 * Above this count the Evidence power-user banner replaces the generic trial banner.
 */
export const EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD = 10;

/** localStorage key — cleared on logout (`clearLogoutStorage`). */
export const EVIDENCE_POWER_USER_BANNER_DISMISSED_STORAGE_KEY =
  'heidi.evidencePowerUserBannerDismissed';

/**
 * Whether the user has dismissed the Evidence power-user promo banner that sits at the
 * bottom of the Evidence chat. Persists across reloads so we don't re-nag after a close;
 * cleared on full logout so the next account on this device is not suppressed.
 */
export const evidencePowerUserBannerDismissedAtom = atomWithStorage<boolean>(
  EVIDENCE_POWER_USER_BANNER_DISMISSED_STORAGE_KEY,
  false,
  createSsrSafeStorage<boolean>()
);

/**
 * Non-production escape hatch to turn the Evidence power-user feature on without touching
 * PostHog or setting `NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS`. Consumed only by
 * `useIsEvidencePowerUserEnabled`, which ignores this value in production builds.
 * Persists across reloads for convenience when iterating on the feature.
 */
export const evidencePowerUserDevForceEnabledAtom = atomWithStorage<boolean>(
  'heidi.evidencePowerUserDevForceEnabled',
  false,
  createSsrSafeStorage<boolean>()
);

/** localStorage key — cleared on logout (`clearLogoutStorage`). */
export const EVIDENCE_POWER_USER_DEV_FORCE_SHOW_BANNER_STORAGE_KEY =
  'heidi.evidencePowerUserDevForceShowBanner';

/**
 * Non-production dev-tools escape hatch: when true, the Evidence power-user chat banner
 * is shown regardless of PostHog flag, dismissal, usage threshold, or trial segment.
 * Ignored in production builds (see `EvidencePowerUserBanner` visibility).
 */
export const evidencePowerUserDevForceShowBannerAtom = atomWithStorage<boolean>(
  EVIDENCE_POWER_USER_DEV_FORCE_SHOW_BANNER_STORAGE_KEY,
  false,
  createSsrSafeStorage<boolean>()
);
